import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../../../assets";
import { TaskConstants } from "../../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType } from "../../../../../../models";
import { IStateOrigin } from "../../../../../../navigators/navigateStateObjects";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../../service/query";
import { ResultData } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { flattenObject } from "../../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../../../../../widget";
import { AvatarCellFormatter } from "../../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconLinkCellFormatterData, LinkCellFormatterData } from "../../../../../../widget/data/DataGrid/models";

interface UseUpcomingTasksPanelReturnData {
  handleCloseWorkflowTaskPanel: () => void;
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useUpcomingTasksPanel = (
  groupUuid: string | undefined,
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void,
  setShowWorkflowTaskPanel: (show: boolean) => void
): UseUpcomingTasksPanelReturnData => {
  const location = useLocation();
  const { currentUserType } = useAuth();

  const [dataIsLoading, setDataIsLoading] = useState(true);

  const handleCloseWorkflowTaskPanel = (): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  };

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Task",
      key: "taskLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 300,
      sortable: true,
    },
    {
      name: "Project",
      key: "projectLink",
      dataType: "string",
      formatter: "link",
      alignment: "left",
      filterable: true,
      minWidth: 140,
      sortable: true,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
      sortable: true,
    },
    {
      key: "ownerOrganisation.name",
      name: "Owner",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "left",
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "center",
      filterable: true,
      minWidth: 140,
      sortable: true,
    },
    {
      name: "Deadline",
      key: "deadlineDate",
      dataType: "string",
      formatter: "dateOnlyWithIndicator",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: true,
    },
    {
      name: "Created at",
      key: "createdAt",
      dataType: "string",
      formatter: "dateOnly",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: true,
    },
    {
      key: "viewTasksBoardIcon",
      name: "View tasks board",
      dataType: "string",
      formatter: "iconLink",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
  ];

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        result.projectLink = {
          text: d.workflowObjectName,
          to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType),
          state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
        } as LinkCellFormatterData;

        result.viewTasksBoardIcon = {
          to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType),
          state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
          icon: <ArrowheadRightDarkIcon />,
        } as IconLinkCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    if (groupUuid) {
      filterCriteria.groups = {
        some: {
          groupUuid: {
            operator: "eq",
            value: groupUuid,
          },
        },
      };
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.projectLink) {
      filterCriteria.projectName = filterCriteria.projectLink;
      filterCriteria.projectLink = undefined;
      delete filterCriteria.projectLink;
    }

    const sortKeyMap: Record<string, string> = {
      taskLink: "name",
      avatar: "assignee.name",
      projectLink: "projectName",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    handleCloseWorkflowTaskPanel,
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
  };
};
